// PlayerRequestsPanel — Phase 3.5 consolidated view.
//
// Replaces /finance/players for the agent SPA. Structure (top to bottom):
//   1. QRCodeModule           — what players see + inline edit
//   2. Recharge / Withdrawal  — magenta hero toggle
//   3. ManualRechargeForm     — only on Recharge view
//   4. Pending / History      — magenta sub-tabs
//   5. Request cards          — recharge or withdrawal queue rows
//
// Wallet sync (non-negotiable, per Phase 3.5 brief):
//   - refreshAgentWallet() on mount (auto-refetch on navigation to /people/players)
//   - refreshAgentWallet() after every successful approve / reject / mark-paid
//   - ManualRechargeForm calls refreshAgentWallet() itself on send
//
// Lighter than MoneyPanel: this panel handles ONLY the player-recharge
// and player-withdrawal kinds. The sub-agent flows continue to live at
// /finance/agents until they get the same treatment in a later phase.

import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import WhatsAppIcon from '@baaligames/shared-ui/components/WhatsAppIcon';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import ApprovalEditModal from '../../components/ApprovalEditModal';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import QRCodeModule from '../../components/QRCodeModule';
import ProofImageViewer, { type ProofState } from '@baaligames/shared-ui/components/ProofImageViewer';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';
import ProofImagePickerChip from '@baaligames/shared-ui/components/ProofImagePickerChip';
// 2026-05-16: replaced ManualRechargeForm with DirectAdjustCard —
// same backend endpoint, but the new card matches the admin SPA's
// compact "tap to browse → search → pick → modal" pattern and
// surfaces the rich subject identity card (with PLAYER badge,
// display name, wallet, and a "your pool" funding-source chip) so
// the agent visually confirms the right entity before submitting.
import DirectAdjustCard from '../../components/DirectAdjustCard';

// ─── Types ─────────────────────────────────────────────────────────

// Phase 18 — proof columns. The list endpoints SELECT rr.* / wr.*
// so these flow through automatically. Frontend computes state
// inline from the *_uploaded_at timestamps.
type ProofColumns = {
  proof_image_url: string | null;
  proof_uploaded_at: string | null;
  payout_proof_url: string | null;
  payout_proof_uploaded_at: string | null;
  payout_proof_view_count: number;
};
type RechargeRow = ProofColumns & {
  id: number;
  user_id: number;
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_username: string;
  user_email: string;
  user_phone: string;
  amount: number;
  approved_amount: number | null;
  transaction_id: string;
  user_note: string | null;
  state: 'pending'|'approved'|'rejected'|'expired';
  decided_at: string | null;
  decision_note: string | null;
  ledger_id: number | null;
  attached_offer_claim_id: number | null;
  created_at: string;
};

type WithdrawalRow = ProofColumns & {
  id: number;
  user_id: number;
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_username: string;
  user_phone: string;
  amount: number;
  approved_amount: number | null;
  upi_id: string;
  whatsapp_number: string | null;
  qr_image_url: string | null;
  state: 'pending'|'approved'|'rejected'|'paid'|'expired';
  decided_at: string | null;
  decision_note: string | null;
  paid_transaction_id: string | null;
  paid_at: string | null;
  hold_ledger_id: number | null;
  created_at: string;
};

type Direction = 'recharge' | 'withdrawal';
type Sub = 'pending' | 'history';

const LIMIT = 50;
const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

// ─── Phase 18 — proof helpers (mirror of admin-frontend) ──────────
const VIEW_EXPIRY_MS   = 72 * 60 * 60 * 1000;
const UPLOAD_WINDOW_MS = 24 * 60 * 60 * 1000;
function proofStateFor(uploadedAt: string | null, url: string | null): ProofState {
  if (!uploadedAt) return 'none';
  const age = Date.now() - new Date(uploadedAt).getTime();
  if (age > VIEW_EXPIRY_MS) return 'expired';
  return url ? 'available' : 'expired';
}
function canUploadPayoutProof(paidAt: string | null, state: string, hasProof: boolean): boolean {
  if (hasProof) return false;
  if (state !== 'paid') return false;
  if (!paidAt) return false;
  return (Date.now() - new Date(paidAt).getTime()) < UPLOAD_WINDOW_MS;
}

/**
 * PayoutProofCell (agent variant). Renders one of:
 *   - nothing (state !== 'paid')
 *   - the viewer (payout_proof_url already set)
 *   - an upload picker (within 24h of paid_at, no proof yet)
 *   - "Upload window closed" pill (past 24h, no proof)
 *
 * Uses the admin payment-requests router endpoints which agent
 * service ALSO mounts at /api/admin/payment-requests/* — same path
 * the admin frontend hits. Server-side auth gate verifies the
 * caller is the original approver (decided_by_agent_id).
 */
function PayoutProofCell({
  row, uploadEndpoint, viewEndpoint, onUploaded,
}: {
  row: { state: string; paid_at: string | null;
         payout_proof_url: string | null; payout_proof_uploaded_at: string | null };
  uploadEndpoint: string;
  viewEndpoint: string;
  onUploaded?: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (row.state !== 'paid') return null;
  const proofState = proofStateFor(row.payout_proof_uploaded_at, row.payout_proof_url);
  if (proofState === 'available' || proofState === 'expired') {
    return <ProofImageViewer state={proofState} endpoint={viewEndpoint} apiGet={(p) => adminApi.get(p)} />;
  }
  const can = canUploadPayoutProof(row.paid_at, row.state, false);
  if (!can) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold
                       bg-arena-100/70 text-arena-500 ring-1 ring-inset ring-arena-200/50
                       dark:bg-arena-700/40 dark:text-neon-200/55 dark:ring-arena-600/40">
        Upload window closed
      </span>
    );
  }
  async function send(blob: Blob) {
    setUploading(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('proof', blob, 'payout.jpg');
      await adminApi.postForm(uploadEndpoint, fd);
      onUploaded?.();
    } catch (e: any) {
      setErr(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }
  return (
    // Compact chip — keeps the card's narrow right column from
    // ballooning to ~160 px on mobile (the picker's natural width)
    // and squeezing the content column to <100 px. The full picker
    // (label + hint + preview) is reserved for the mark-paid modal
    // and dedicated upload forms where horizontal room exists.
    <span className="inline-flex items-center gap-2 flex-wrap justify-end">
      <ProofImagePickerChip
        onFile={(b) => send(b)}
        onError={(m) => setErr(m)}
        disabled={uploading}
      />
      {err && <span className="text-[11px] text-rose-700 dark:text-rose-300">{err}</span>}
    </span>
  );
}

// ─── Panel ─────────────────────────────────────────────────────────

export default function PlayerRequestsPanel() {
  const t = useT();
  // When a player detail page's "Recharge" CTA deep-links here with
  // ?preselect=<id>, force the Recharge direction AND hand the id to
  // DirectAdjustCard, which resolves that player and pops the recharge
  // modal open immediately (2026-08-06 UX fix — no re-searching).
  const [searchParams] = useSearchParams();
  const preselectId = Number(searchParams.get('preselect')) || undefined;
  const hasPreselect = !!preselectId;
  const [direction, setDirection] = useState<Direction>('recharge');
  const [sub, setSub] = useState<Sub>('pending');

  useEffect(() => {
    if (hasPreselect) setDirection('recharge');
  }, [hasPreselect]);

  const [counts, setCounts] = useState<{ recharge: number; withdrawal: number }>({ recharge: 0, withdrawal: 0 });

  const refreshCounts = useCallback(async () => {
    try {
      const [r, w] = await Promise.all([
        adminApi.get<{ total: number }>(`/admin/payment-requests/recharge?state=pending&limit=1&offset=0`),
        adminApi.get<{ total: number }>(`/admin/payment-requests/withdrawal?state=pending&limit=1&offset=0`),
      ]);
      setCounts({ recharge: r.total, withdrawal: w.total });
    } catch { /* surfaced in queue */ }
  }, []);

  // Wallet sync — auto-refetch on mount (navigation into this page)
  // PLUS refresh the pending counts.
  useEffect(() => {
    refreshAgentWallet();
    refreshCounts();
  }, [refreshCounts]);

  return (
    <div className="space-y-4">
      {/* 1. QR module — sky accent so the page colour matches the
          Players section bubble in the bottom nav. */}
      <QRCodeModule audience="players" accent="sky" />

      {/* 2. Recharge / Withdrawal hero toggle */}
      <MagentaTabBar
        size="lg"
        accent="sky"
        activeKey={direction}
        onChange={(k) => { setDirection(k as Direction); setSub('pending'); }}
        items={[
          // Phase R7 follow-up (2026-05-13): per-direction pending
          // counts so the workload split is visible without
          // flipping tabs. Magenta badges (handled in MagentaTabBar)
          // pop against the sky accent fill.
          { key: 'recharge',   label: t('agent.playerRequests.direction.recharge'),   icon: 'arrow_upward',   count: counts.recharge },
          { key: 'withdrawal', label: t('agent.playerRequests.direction.withdrawal'), icon: 'arrow_downward', count: counts.withdrawal },
        ]}
      />

      {/* 3. Direct recharge card — recharge view only.
             Withdrawals can never go through here per business rule:
             agents may only release funds to players via the request
             queue (the player initiates by submitting a withdrawal
             request). So we conditionally render only on Recharge. */}
      {direction === 'recharge' && (
        <DirectAdjustCard scope="players" onAfterAdjust={refreshCounts} preselectId={preselectId} />
      )}

      {/* 4. Pending / History sub-tabs */}
      <MagentaTabBar
        size="sm"
        accent="sky"
        activeKey={sub}
        onChange={(k) => setSub(k as Sub)}
        items={[
          { key: 'pending', label: t('agent.playerRequests.tab.pending'), icon: 'inbox',   count: counts[direction] },
          { key: 'history', label: t('agent.playerRequests.tab.history'), icon: 'history' },
        ]}
      />

      {/* 5. Queue */}
      <Queue direction={direction} sub={sub} onChanged={refreshCounts} />
    </div>
  );
}

// ─── Queue body ────────────────────────────────────────────────────

function Queue({
  direction, sub, onChanged,
}: {
  direction: Direction; sub: Sub; onChanged: () => void;
}) {
  const t = useT();
  const [rRows, setRRows]   = useState<RechargeRow[]>([]);
  const [wRows, setWRows]   = useState<WithdrawalRow[]>([]);
  const [total, setTotal]   = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const endpoint = direction === 'recharge'
    ? '/admin/payment-requests/recharge'
    : '/admin/payment-requests/withdrawal';

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      if (sub === 'pending') {
        const r = await adminApi.get<{ rows: any[]; total: number }>(`${endpoint}?state=pending&limit=${LIMIT}&offset=${offset}`);
        if (direction === 'recharge') setRRows(r.rows as RechargeRow[]);
        else                          setWRows(r.rows as WithdrawalRow[]);
        setTotal(r.total);
      } else {
        // History — chronological merge of all terminal states.
        const states = direction === 'recharge'
          ? ['approved','rejected']
          : ['paid','rejected'];
        const pages = await Promise.all(
          states.map((s) => adminApi.get<{ rows: any[]; total: number }>(`${endpoint}?state=${s}&limit=${LIMIT}&offset=${offset}`))
        );
        const merged = pages.flatMap((p) => p.rows).sort((x: any, y: any) => y.created_at.localeCompare(x.created_at));
        if (direction === 'recharge') setRRows(merged as RechargeRow[]);
        else                          setWRows(merged as WithdrawalRow[]);
        setTotal(pages.reduce((a, p) => a + p.total, 0));
      }
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }, [direction, sub, offset, endpoint]);

  useEffect(() => { setOffset(0); }, [direction, sub]);
  useEffect(() => { load(); }, [load]);

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Approval edit modal state ──
  type ModalCtx =
    | { kind: 'recharge';   row: RechargeRow }
    | { kind: 'withdrawal'; row: WithdrawalRow }
    | null;
  const [modal, setModal] = useState<ModalCtx>(null);
  const [rejectCtx, setRejectCtx] = useState<{ kind: 'recharge' | 'withdrawal'; id: number } | null>(null);

  function openApproveRecharge(id: number) {
    const row = rRows.find((r) => r.id === id);
    if (row) setModal({ kind: 'recharge', row });
  }
  function openMarkPaid(row: WithdrawalRow) {
    setModal({ kind: 'withdrawal', row });
  }

  // One-tap: approve a recharge for the FULL requested amount without
  // opening the edit modal (2026-08-06 UX fix — the modal was pure
  // friction for the common "approve exactly what they asked" case).
  // The "Edit" button still opens the modal for partial approvals. The
  // money path is identical to the modal's full-amount approve.
  async function approveRechargeFull(row: RechargeRow) {
    const id = row.id;
    setBusyId(id); setErr(null);
    try {
      const r = await adminApi.post<{ bonus: { amount: number; ledgerId: number } | null }>(
        `/admin/payment-requests/recharge/${id}/approve`, { approved_amount: row.amount }
      );
      setToast(r.bonus
        ? t('agent.playerRequests.toast.approvedWithBonus', { amount: inr(row.amount), bonus: String(r.bonus.amount) })
        : t('agent.playerRequests.toast.approved', { amount: inr(row.amount) }));
      refreshAgentWallet();
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  }

  // Reject is a two-step now: the row's Reject button opens a themed
  // reason sheet (2026-08-06 UX fix — replaced window.prompt, which is
  // off-theme and a no-op in many Android webviews). The reason stays
  // OPTIONAL. confirmReject does the actual POST on Confirm.
  function rejectRecharge(id: number)   { setRejectCtx({ kind: 'recharge',   id }); }
  function rejectWithdrawal(id: number) { setRejectCtx({ kind: 'withdrawal', id }); }

  async function confirmReject(reason?: string) {
    if (!rejectCtx) return;
    const { kind, id } = rejectCtx;
    const note = (reason || '').trim();
    setBusyId(id); setErr(null);
    try {
      const path = kind === 'recharge'
        ? `/admin/payment-requests/recharge/${id}/reject`
        : `/admin/payment-requests/withdrawal/${id}/reject`;
      await adminApi.post(path, note ? { note } : {});
      setToast(kind === 'recharge'
        ? t('agent.playerRequests.toast.rejected')
        : t('agent.playerRequests.toast.rejectedRefunded'));
      refreshAgentWallet();
      setRejectCtx(null);
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); throw e; }
    finally { setBusyId(null); }
  }

  async function onApprovalConfirm(args: { approvedAmount: number; note?: string; paidTransactionId?: string; payoutProofBlob?: Blob | null }) {
    if (!modal) return;
    const id = modal.row.id;
    setBusyId(id); setErr(null);
    try {
      const body: Record<string, any> = { approved_amount: args.approvedAmount };
      if (args.note) body.note = args.note;
      let toastMsg = '';
      let payoutProofUrlPath: string | null = null;
      if (modal.kind === 'recharge') {
        const r = await adminApi.post<{ bonus: { amount: number; ledgerId: number } | null }>(
          `/admin/payment-requests/recharge/${id}/approve`, body
        );
        toastMsg = r.bonus
          ? t('agent.playerRequests.toast.approvedWithBonus', { amount: inr(args.approvedAmount), bonus: String(r.bonus.amount) })
          : t('agent.playerRequests.toast.approved', { amount: inr(args.approvedAmount) });
      } else {
        body.paid_transaction_id = args.paidTransactionId!;
        await adminApi.post(`/admin/payment-requests/withdrawal/${id}/mark-paid`, body);
        toastMsg = t('agent.playerRequests.toast.markedPaid', { amount: inr(args.approvedAmount) });
        payoutProofUrlPath = `/admin/payment-requests/withdrawal/${id}/payout-proof`;
      }
      // Phase 18 — chain the payout-proof upload if attached.
      if (args.payoutProofBlob && payoutProofUrlPath) {
        try {
          const fd = new FormData();
          fd.append('proof', args.payoutProofBlob, 'payout.jpg');
          await adminApi.postForm(payoutProofUrlPath, fd);
          toastMsg += ' · screenshot uploaded';
        } catch (_uploadErr) {
          setErr('Marked paid, but screenshot upload failed. Retry from the queue card within 24 h.');
        }
      }
      // Wallet sync — every approve / mark-paid moves money in or out
      // of the agent's pool. Refresh the global chip immediately.
      refreshAgentWallet();
      setToast(toastMsg);
      setModal(null);
      await load(); onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
      throw e;
    } finally {
      setBusyId(null);
    }
  }

  const rows: Array<RechargeRow | WithdrawalRow> = direction === 'recharge' ? rRows : wRows;

  const modalRequested = modal?.row.amount ?? 0;
  const modalLabel = modal == null ? '' :
    modal.kind === 'recharge'
      ? `@${(modal.row as RechargeRow).user_username}`
      : `@${(modal.row as WithdrawalRow).user_username}`;

  return (
    <div className="space-y-3">
      <ApprovalEditModal
        open={modal !== null}
        kind={modal?.kind === 'withdrawal' ? 'withdrawal' : 'recharge'}
        requestedAmount={modalRequested}
        counterpartyLabel={modalLabel}
        onCancel={() => setModal(null)}
        onConfirm={onApprovalConfirm}
      />

      {/* Themed reject-reason sheet (replaces window.prompt). Reason is
          optional; withdrawal rejects refund the player's held balance. */}
      <DangerConfirmModal
        open={rejectCtx !== null}
        onClose={() => setRejectCtx(null)}
        onConfirm={confirmReject}
        title={rejectCtx?.kind === 'withdrawal'
          ? t('agent.playerRequests.reject.title.withdrawal')
          : t('agent.playerRequests.reject.title.recharge')}
        description={rejectCtx?.kind === 'withdrawal'
          ? t('agent.playerRequests.reject.body.withdrawal')
          : t('agent.playerRequests.reject.body.recharge')}
        confirmLabel={t('agent.playerRequests.action.reject')}
        reasonLabel={t('agent.playerRequests.reject.reasonLabel')}
        reasonPlaceholder={t('agent.playerRequests.reject.reasonPlaceholder')}
      />

      {err && <Banner tone="error">{err}</Banner>}
      {toast && <Banner tone="success">{toast}</Banner>}

      {loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 p-8 text-center text-[13px] text-arena-600 dark:text-neon-200/80">
          {t('agent.playerRequests.loading')}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 p-8 text-center">
          <Icon name="inbox" size={28} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
          <div className="text-[13px] text-arena-600 dark:text-neon-200/80">
            {sub === 'pending'
              ? (direction === 'recharge'
                  ? t('agent.playerRequests.empty.pending.recharge')
                  : t('agent.playerRequests.empty.pending.withdrawal'))
              : t('agent.playerRequests.empty.history')}
          </div>
        </div>
      )}

      {/* Phase 3.5 refinement: single full-width column at every
          viewport. The 2-up `lg:grid-cols-2` layout was visually
          worse on large screens because each card was narrow and
          the action buttons started wrapping awkwardly. Stretch
          horizontally — same shape as mobile, scaled up cleanly. */}
      <ul className="grid grid-cols-1 gap-3">
        {rows.map((row) => (
          <li key={row.id}>
            {direction === 'recharge' ? (
              <RechargeCard
                row={row as RechargeRow}
                busy={busyId === row.id}
                onApprove={openApproveRecharge}
                onApproveFull={approveRechargeFull}
                onReject={rejectRecharge}
              />
            ) : (
              <WithdrawalCard
                row={row as WithdrawalRow}
                busy={busyId === row.id}
                onMarkPaid={openMarkPaid}
                onReject={rejectWithdrawal}
                onChanged={load}
              />
            )}
          </li>
        ))}
      </ul>

      {total > LIMIT && (
        <div className="flex items-center justify-end gap-2 pt-2 text-[12px] text-arena-600 dark:text-neon-200/80">
          <button disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            className="grid place-items-center w-8 h-8 rounded-full bg-white/70 dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 disabled:opacity-40 hover:bg-white dark:hover:bg-arena-600/55">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums">{t('agent.peopleQueue.pagination', { from: String(offset + 1), to: String(Math.min(offset + LIMIT, total)), total: String(total) })}</span>
          <button disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
            className="grid place-items-center w-8 h-8 rounded-full bg-white/70 dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 disabled:opacity-40 hover:bg-white dark:hover:bg-arena-600/55">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Cards (slimmed for player flows only) ─────────────────────────

function CardShell({ children, accent }: { children: ReactNode; accent: 'in' | 'out' }) {
  const ring = accent === 'in'
    ? 'ring-emerald-200/70 dark:ring-emerald-400/20'
    : 'ring-rose-200/70 dark:ring-rose-400/20';
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-4
                  bg-white dark:bg-arena-700/40
                  border border-arena-200/60 dark:border-arena-600/40 ring-1 ${ring}
                  shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none`}
    >
      {children}
    </motion.div>
  );
}

function FlowChip({ dir, amount }: { dir: 'in' | 'out'; amount: number }) {
  return (
    <span className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                      text-[14px] font-bold tabular-nums
                      ${dir === 'in'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}`}>
      <Icon name={dir === 'in' ? 'arrow_upward' : 'arrow_downward'} size={14} />
      {inr(amount)}
    </span>
  );
}

// Single chronological history feed needs distinct visual badges so the
// row state reads at a glance: Accepted / Rejected / Manually Paid.
function StateBadge({ state, kind }: { state: string; kind: 'recharge' | 'withdrawal' }) {
  const t = useT();
  const label =
    state === 'pending'  ? t('agent.playerRequests.state.pending')
    : state === 'rejected' ? t('agent.playerRequests.state.rejected')
    : state === 'expired' ? t('agent.playerRequests.state.expired')
    : kind === 'withdrawal' && state === 'paid'     ? t('agent.playerRequests.state.manuallyPaid')
    : kind === 'recharge'   && state === 'approved' ? t('agent.playerRequests.state.accepted')
    : state;
  const tone =
    state === 'pending'  ? 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/30'
    : state === 'rejected' ? 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-400/15 dark:text-rose-200 dark:border-rose-400/30'
    : state === 'expired' ? 'bg-arena-100/70 text-arena-700 border-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50'
    : 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-200 dark:border-emerald-400/30';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold border ${tone}`}>
      {label}
    </span>
  );
}

function ActionBtn({ tone, icon, label, onClick, busy }: {
  tone: 'success' | 'danger' | 'neutral'; icon: string; label: string; onClick: () => void; busy?: boolean;
}) {
  const M = {
    success: 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700',
    danger:  'bg-white text-rose-700 border border-rose-200 hover:bg-rose-50 dark:bg-arena-700/40 dark:text-rose-300 dark:border-rose-400/30 dark:hover:bg-rose-400/10',
    neutral: 'bg-white text-arena-700 border border-arena-200/60 hover:bg-arena-50/40 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50 dark:hover:bg-arena-600/55',
  } as const;
  return (
    <button onClick={onClick} disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition disabled:opacity-50 active:scale-95 ${M[tone]}`}>
      <Icon name={busy ? 'progress_activity' : icon} size={14} className={busy ? 'animate-spin' : ''} />
      {label}
    </button>
  );
}

function RechargeCard({ row, busy, onApprove, onApproveFull, onReject }: {
  row: RechargeRow; busy: boolean;
  onApprove: (id: number) => void; onApproveFull: (row: RechargeRow) => void; onReject: (id: number) => void;
}) {
  const t = useT();
  return (
    <CardShell accent="in">
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={null} username={row.user_username} size="sm" />
        <div className="min-w-0 flex-1">
          <Link to={`/people/players/${row.user_id}`} className="text-[13.5px] font-semibold text-arena-900 dark:text-white truncate hover:underline block">
            {(((row.user_first_name || "") + " " + (row.user_last_name || "")).trim() || "—")}
          </Link>
          {/* Phase C4 — Identity Stack. Player is a DIRECT downline of
              the calling agent (the queue endpoint scopes to agent_id =
              caller). Full visibility: @username + phone. */}
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{row.user_username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{row.user_id}
          </div>
          <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums">
            {row.user_phone
              ? <PhoneContact phone={row.user_phone} name={row.user_username} iconSize={11} className="tabular-nums" />
              : row.user_email}
          </div>
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/65 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        {/* Phase 18 UI fix — proof affordance lives in the top-right
            column, maximally far from the bottom-right Approve button. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="in" amount={row.amount} />
          <ProofImageViewer
            state={proofStateFor(row.proof_uploaded_at, row.proof_image_url)}
            endpoint={`/admin/payment-requests/recharge/${row.id}/proof`}
            apiGet={(p) => adminApi.get(p)} />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/60 dark:bg-arena-700/30 border border-arena-200/60 dark:border-arena-600/50 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/80">{t('agent.playerRequests.card.upiTxnLabel')}</span>
          <span className="font-mono text-arena-900 dark:text-white truncate">{row.transaction_id}</span>
        </div>
        {row.user_note && (
          <div className="text-[11px] text-arena-700 dark:text-neon-200/80 italic">"{row.user_note}"</div>
        )}
        {row.approved_amount != null && row.approved_amount !== row.amount && (
          <div className="text-[11px] text-amber-700 dark:text-amber-300">
            {t('agent.playerRequests.card.partialApproved', { approved: inr(row.approved_amount), requested: inr(row.amount) })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StateBadge state={row.state} kind="recharge" />
          {row.attached_offer_claim_id && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold
                             bg-violet-50 text-violet-800 border border-violet-200
                             dark:bg-violet-400/10 dark:text-violet-200 dark:border-violet-400/30">
              <Icon name="redeem" size={11} filled /> {t('agent.playerRequests.card.bonusChip')}
            </span>
          )}
        </div>

        {row.state === 'pending' ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close" label={t('agent.playerRequests.action.reject')} onClick={() => onReject(row.id)} busy={busy} />
            {/* Edit = open the modal to approve a PARTIAL amount (rare). */}
            <ActionBtn tone="neutral" icon="tune"  label={t('agent.playerRequests.action.edit')} onClick={() => onApprove(row.id)} busy={busy} />
            {/* Primary one-tap: approve the FULL requested amount. */}
            <ActionBtn tone="success" icon="check" label={`${t('agent.playerRequests.action.approve')} ${inr(row.amount)}`} onClick={() => onApproveFull(row)} busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/80 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

function WithdrawalCard({ row, busy, onMarkPaid, onReject, onChanged }: {
  row: WithdrawalRow; busy: boolean;
  onMarkPaid: (row: WithdrawalRow) => void; onReject: (id: number) => void;
  onChanged?: () => void;
}) {
  const t = useT();
  return (
    <CardShell accent="out">
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={null} username={row.user_username} size="sm" />
        <div className="min-w-0 flex-1">
          <Link to={`/people/players/${row.user_id}`} className="text-[13.5px] font-semibold text-arena-900 dark:text-white truncate hover:underline block">
            {(((row.user_first_name || "") + " " + (row.user_last_name || "")).trim() || "—")}
          </Link>
          {/* Phase C4 — Identity Stack (Name / @username / phone). */}
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{row.user_username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{row.user_id}
          </div>
          <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums">
            {row.user_phone
              ? <PhoneContact phone={row.user_phone} name={row.user_username} iconSize={11} className="tabular-nums" />
              : ''}
          </div>
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/65 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        {/* Phase 18 UI fix — payout-proof cell in the top-right column,
            spatially separated from the bottom-right Mark Paid action. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="out" amount={row.amount} />
          <PayoutProofCell
            row={row}
            uploadEndpoint={`/admin/payment-requests/withdrawal/${row.id}/payout-proof`}
            viewEndpoint={`/admin/payment-requests/withdrawal/${row.id}/payout-proof`}
            onUploaded={() => onChanged?.()}
          />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/60 dark:bg-arena-700/30 border border-arena-200/60 dark:border-arena-600/50 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/80">{t('agent.playerRequests.card.payToUpiLabel')}</span>
          <span className="font-mono text-arena-900 dark:text-white truncate">{row.upi_id}</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {row.whatsapp_number && (
            <a href={`https://wa.me/${row.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
               className="text-[11px] inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 hover:underline">
              <WhatsAppIcon size={11} /> {t('agent.playerRequests.card.whatsapp')}
            </a>
          )}
          {row.qr_image_url && (
            <a href={row.qr_image_url} target="_blank" rel="noreferrer"
               className="text-[11px] inline-flex items-center gap-1 text-cyan-700 dark:text-cyan-300 hover:underline">
              <Icon name="qr_code_2" size={11} /> {t('agent.playerRequests.card.viewQr')}
            </a>
          )}
        </div>
        {row.approved_amount != null && row.approved_amount !== row.amount && (
          <div className="text-[11px] text-amber-700 dark:text-amber-300">
            {t('agent.playerRequests.card.partialPaid', { approved: inr(row.approved_amount), requested: inr(row.amount), refund: inr(row.amount - row.approved_amount) })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StateBadge state={row.state} kind="withdrawal" />
          {row.paid_transaction_id && row.state === 'paid' && (
            <span className="text-[10.5px] text-emerald-700 dark:text-emerald-300 font-mono truncate">{t('agent.playerRequests.card.paidTxnPrefix', { txn: row.paid_transaction_id })}</span>
          )}
        </div>

        {(row.state === 'pending' || row.state === 'approved') ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close"    label={t('agent.playerRequests.action.reject')}   onClick={() => onReject(row.id)} busy={busy} />
            <ActionBtn tone="success" icon="payments" label={t('agent.playerRequests.action.markPaid')} onClick={() => onMarkPaid(row)}  busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/80 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success'; children: ReactNode }) {
  const M = {
    error:   'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200',
  } as const;
  return (
    <div className={`rounded-2xl border px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>
  );
}
